import {
  WardrobeTemplate,
  WardrobeZoneType,
  WardrobeZone,
} from '~/types/wardrobeConfigurationTypes'

export enum WardrobeTemplateId {
  FULL_HANGING_WITH_1_SHELF = 'FULL_HANGING_WITH_1_SHELF',
  FULL_HANGING_WITH_2_DRAWERS = 'FULL_HANGING_WITH_2_DRAWERS',
  SHELVES_ONLY = 'SHELVES_ONLY',
  MIXED_STORAGE_COMPLEX = 'MIXED_STORAGE_COMPLEX',
  HANGING_WITH_4_DRAWERS = 'HANGING_WITH_4_DRAWERS',
  HANGING_WITH_2_DRAWERS = 'HANGING_WITH_2_DRAWERS',
  DOUBLE_HANGING = 'DOUBLE_HANGING',
  DOUBLE_HANGING_PREMIUM_TOP = 'DOUBLE_HANGING_PREMIUM_TOP',
  DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS = 'DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS',
  ONE_TOP_SHELF = 'ONE_TOP_SHELF',
}

/**
 * Predefined wardrobe column templates
 * Based on common wardrobe configurations and user sketches
 *
 * IMPORTANT: Zones are listed in TOP-TO-BOTTOM order
 * - First zone in array = top of wardrobe
 * - Last zone in array = bottom of wardrobe
 *
 * All templates are designed for 196 cm but scale to fit available height
 */
export const WARDROBE_TEMPLATES: Record<string, WardrobeTemplate> = {
  [WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF]: {
    id: WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF,
    name: 'Full Hanging with 1 Shelf',
    description:
      'Full-height hanging space for long dresses and coats with 1 shelf at bottom',
    zones: [
      {
        type: WardrobeZoneType.HANGING_LONG,
        height: 156,
        rodHeight: 148,
      },
      {
        type: WardrobeZoneType.SHELVES_FIXED,
        height: 40,
        shelfCount: 1,
        shelfSpacing: 40,
      },
    ],
    minHeight: 180,
    minWidth: 40,
    tags: ['dresses', 'coats', 'womens'],
    icon: 'hanging-long',
    extraCost: 450,
  },

  [WardrobeTemplateId.FULL_HANGING_WITH_2_DRAWERS]: {
    id: WardrobeTemplateId.FULL_HANGING_WITH_2_DRAWERS,
    name: 'Full Hanging with 2 Drawers',
    description:
      'Full-height hanging space for long dresses and coats with 2 drawers at bottom',
    zones: [
      {
        type: WardrobeZoneType.HANGING_LONG,
        height: 156,
        rodHeight: 148,
      },
      {
        type: WardrobeZoneType.DRAWERS,
        height: 40,
        drawerCount: 2,
        drawerHeights: [15, 15],
        addShelfAtTheTop: true,
      },
    ],
    minHeight: 180,
    minWidth: 40,
    tags: ['dresses', 'coats', 'womens'],
    icon: 'hanging-long',
    extraCost: 1000,
  },

  [WardrobeTemplateId.SHELVES_ONLY]: {
    id: WardrobeTemplateId.SHELVES_ONLY,
    name: 'Shelves Only',
    description: 'Multiple adjustable shelves for folded items',
    zones: [
      {
        type: WardrobeZoneType.SHELVES,
        height: 200,
        shelfCount: 4,
      },
    ],
    minHeight: 170,
    minWidth: 40,
    tags: ['storage', 'folded', 'organized'],
    icon: 'shelves',
    extraCost: 600,
  },

  [WardrobeTemplateId.MIXED_STORAGE_COMPLEX]: {
    id: WardrobeTemplateId.MIXED_STORAGE_COMPLEX,
    name: 'Mixed Storage',
    description: 'Combination of shelves and drawers with varied heights',
    zones: [
      {
        type: WardrobeZoneType.SHELVES,
        height: 120,
        shelfCount: 2,
      },
      {
        type: WardrobeZoneType.DRAWERS,
        height: 80,
        drawerCount: 4,
        drawerHeights: [15, 15, 15, 15],
        addShelfAtTheTop: true,
      },
    ],
    minHeight: 170,
    minWidth: 40,
    tags: ['complex', 'custom', 'versatile'],
    icon: 'mixed-complex',
    extraCost: 2300,
  },

  [WardrobeTemplateId.HANGING_WITH_4_DRAWERS]: {
    id: WardrobeTemplateId.HANGING_WITH_4_DRAWERS,
    name: 'Hanging + 4 Drawers',
    description: 'Hanging space on top with 4 drawers storage below',
    zones: [
      {
        type: WardrobeZoneType.HANGING_LONG,
        height: 120,
        rodHeight: 109,
      },
      {
        type: WardrobeZoneType.DRAWERS,
        height: 80,
        drawerCount: 4,
        drawerHeights: [15, 15, 15, 15],
        addShelfAtTheTop: true,
      },
    ],
    minHeight: 160,
    minWidth: 40,
    tags: ['mixed', 'versatile'],
    icon: 'hanging-drawers',
    extraCost: 2300,
  },

  [WardrobeTemplateId.HANGING_WITH_2_DRAWERS]: {
    id: WardrobeTemplateId.HANGING_WITH_2_DRAWERS,
    name: 'Hanging + 2 Drawers',
    description: 'Hanging space on top with2 drawers storage below',
    zones: [
      {
        type: WardrobeZoneType.HANGING_LONG,
        height: 120,
        rodHeight: 109,
      },
      {
        type: WardrobeZoneType.DRAWERS,
        height: 35,
        drawerCount: 2,
        drawerHeights: [12, 12],
        addShelfAtTheTop: true,
      },
      {
        type: WardrobeZoneType.EMPTY,
        height: 45,
      },
    ],
    minHeight: 160,
    minWidth: 40,
    tags: ['mixed', 'versatile'],
    icon: 'hanging-drawers',
    extraCost: 2300,
  },

  // SPECIAL TEMPLATES RENDERED SEPARATELY
  [WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP]: {
    id: WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP,
    name: 'Double Hanging Premium TOP',
    description: 'Two hanging rods, but the top one is premium',
    zones: [
      {
        type: WardrobeZoneType.HANGING_TOP_PREMIUM,
        height: 90,
        rodHeight: 89,
      },
      {
        type: WardrobeZoneType.HANGING_MEDIUM,
        height: 100,
        rodHeight: 99,
      },
    ],
    minHeight: 180,
    minWidth: 60,
    tags: ['shirts', 'pants', 'mens', 'organized'],
    icon: 'hanging-double',
    extraCost: 600,
    newCollectionOnly: true,
  },

  [WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS]: {
    id: WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS,
    name: 'Double Hanging Premium TOP with 3 Drawers',
    description: 'Two hanging rods, but the top one is premium with 3 drawers',
    zones: [
      {
        type: WardrobeZoneType.HANGING_TOP_PREMIUM,
        height: 90,
        rodHeight: 89,
      },
      {
        type: WardrobeZoneType.HANGING_MEDIUM,
        height: 100,
        rodHeight: 99,
      },
      {
        type: WardrobeZoneType.DRAWERS,
        height: 55,
        drawerCount: 3,
        drawerHeights: [19, 19, 19],
        addShelfAtTheTop: true,
      },
    ],
    minHeight: 248,
    minWidth: 60,
    tags: ['shirts', 'pants', 'mens', 'organized'],
    icon: 'hanging-double',
    extraCost: 600,
    newCollectionOnly: true,
  },

  //

  [WardrobeTemplateId.DOUBLE_HANGING]: {
    id: WardrobeTemplateId.DOUBLE_HANGING,
    name: 'Double Hanging',
    description: 'Two hanging rods for shirts and folded pants',
    zones: [
      {
        type: WardrobeZoneType.HANGING_MEDIUM,
        height: 100,
        rodHeight: 89,
      },
      {
        type: WardrobeZoneType.HANGING_MEDIUM,
        height: 100,
        rodHeight: 89,
      },
    ],
    minHeight: 180,
    minWidth: 40,
    tags: ['shirts', 'pants', 'mens', 'organized'],
    icon: 'hanging-double',
    extraCost: 600,
  },
  [WardrobeTemplateId.ONE_TOP_SHELF]: {
    id: WardrobeTemplateId.ONE_TOP_SHELF,
    name: 'One top Shelf',
    description: 'One top shelf',
    zones: [
      {
        type: WardrobeZoneType.SHELVES_FIXED,
        height: 80,
        shelfCount: 1,
        shelfSpacing: 40,
      },
      {
        type: WardrobeZoneType.EMPTY,
        height: 120,
      },
    ],
    minHeight: 180,
    minWidth: 40,
    tags: ['shirts', 'pants', 'mens', 'organized'],
    icon: 'hanging-double',
    extraCost: 0,
  },
}

/**
 * Get templates that fit within given dimensions
 */
export function getValidTemplates(
  width: number,
  height: number,
  isNewCollection = false
): WardrobeTemplate[] {
  return Object.values(WARDROBE_TEMPLATES).filter((template) => {
    if (template.newCollectionOnly && !isNewCollection) return false
    const widthValid =
      width >= template.minWidth &&
      (!template.maxWidth || width <= template.maxWidth)
    const heightValid =
      height >= template.minHeight &&
      (!template.maxHeight || height <= template.maxHeight)
    return widthValid && heightValid
  })
}

/**
 * Calculate if a template fits exactly or needs adjustment
 */
export function calculateTemplateAdjustment(
  template: WardrobeTemplate,
  targetHeight: number
): WardrobeZone[] {
  const templateHeight = template.zones.reduce(
    (sum, zone) => sum + zone.height,
    0
  )

  if (templateHeight === targetHeight) {
    return template.zones
  }

  // Proportionally adjust zone heights
  const ratio = targetHeight / templateHeight
  return template.zones.map((zone) => ({
    ...zone,
    height: Math.round(zone.height * ratio),
  }))
}
